/**
* Enums.ts
* @author Abhilash Panwar (abpanwar)
* @copyright Microsoft 2018
* File containing the enums as constants.
*/

import { _InternalMessageId } from "@microsoft/applicationinsights-core-js";

/**
 * The ValueKind contains a set of values that specify value kind of the property.
 * Either PII (Personal Identifiable Information) or customer content.
 */
export const ValueKind = {
    /**
     * No kind.
     */
    NotSet: 0,
    /**
     * An LDAP distinguished name. For example, CN=Jeff Smith,OU=Sales,DC=Fabrikam,DC=COM.
     */
    Pii_DistinguishedName: 1,
    /**
     * Generic information.
     */
    Pii_GenericData: 2,
    /**
     * An IPV4 Internet address. For example, 192.0.2.1.
     */
    Pii_IPV4Address: 3,
    /**
     * An IPV6 Internet address. For example, 2001:0db8:85a3:0000:0000:8a2e:0370:7334.
     */
    Pii_IPv6Address: 4,
    /**
     * The Subject of an e-mail message.
     */
    Pii_MailSubject: 5,
    /**
     * A telephone number.
     */
    Pii_PhoneNumber: 6,
    /**
     * A query string.
     */
    Pii_QueryString: 7,
    /**
     * An SIP (Session Internet Protocol) address.
     */
    Pii_SipAddress: 8,
    /**
     * An e-mail address.
     */
    Pii_SmtpAddress: 9,
    /**
     * An user ID.
     */
    Pii_Identity: 10,
    /**
     * A URI (Uniform Resource Identifier).
     */
    Pii_Uri: 11,
    /**
     * The fully-qualified domain name.
     */
    Pii_Fqdn: 12,
    /**
     * Scrubs the last octet in a IPV4 Internet address.
     * For example: 10.121.227.147 becomes 10.121.227.*
     */
    Pii_IPV4AddressLegacy: 13,
    /**
     * Generic content.
     */
    CustomerContent_GenericContent: 32
};

/**
 * The EventLatency contains a set of values that specify the latency with which an event is sent.
 */
export const EventLatency = {
    /**
     * Normal latency.
     */
    Normal: 1,
    /**
     * Cost deferred latency. At the moment this latency is treated as Normal latency.
     */
    CostDeferred: 2,
    /**
     * Real time latency.
     */
    RealTime: 3
};

/**
 * Enum for property types.
 */
export const EventPropertyType = {
    Unspecified: 0,
    String: 1,
    Int32: 2,
    UInt32: 3,
    Int64: 4,
    UInt64: 5,
    Double: 6,
    Bool: 7,
    Guid: 8,
    DateTime: 9
};

/**
 * The EventPersistence contains a set of values that specify the event's persistence.
 */
export const EventPersistence = {
    /**
     * Normal persistence.
     */
    Normal: 1,
    /**
     * Critical persistence.
     */
    Critical: 2
};

/**
 * The TraceLevel contains a set of values that specify the trace level for the trace messages.
 */
export const TraceLevel = {
    /**
     * None.
     */
    NONE: 0,
    /**
     * Error trace.
     */
    ERROR: 1,
    /**
     * Warning trace.
     */
    WARNING: 2,
    /**
     * Information trace.
     */
    INFORMATION: 3
};

export const _ExtendedInternalMessageId = {
    ..._InternalMessageId,
    AuthHandShakeError: 501,
    AuthRedirectFail: 502,
    BrowserCannotReadLocalStorage: 503,
    BrowserCannotWriteLocalStorage: 504,
    BrowserDoesNotSupportLocalStorage: 505,
    CannotParseBiBlobValue: 506,
    CannotParseDataAttribute: 507,
    CVPluginNotAvailable: 508,
    DroppedEvent: 509,
    ErrorParsingAISessionCookie: 510,
    ErrorProvidedChannels: 511,
    FailedToGetCookies: 512,
    FailedToInitializeCorrelationVector: 513,
    FailedToInitializeSDK: 514,
    InvalidContentBlob: 515,
    InvalidCorrelationValue: 516,
    SessionRenewalDateIsZero: 517,
    SendPostOnCompleteFailure: 518,
    PostResponseHandler: 519
};

// Following the format styles as defined by .Net (https://docs.microsoft.com/en-us/dotnet/api/system.guid.tostring?view=netcore-3.1)
export const enum GuidStyle {
    Numeric = 'N',          // 'N' - 32 digits,
    Digits = 'D',           // 'D' - 32 digits seperated by hypens,
    Braces = 'B',           // 'B' - 32 digits seperated by hypens, enclosed in braces,
    Parentheses = 'P'       // 'P' 32 digits seperated by hypens, enclosed in parentheses
}
